import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  use,
  useCallback,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { toast } from "react-toastify";

import { Question, PollStatus, SurveyStatus } from "../types/valuebuilder";

import Loader from "@/shared/components/Loader";
import { LeftBorder } from "@/shared/components/LeftBorder";
import PollQuestion from "../components/PollQuestion";

import * as m from "@/shared/styles/main.module.scss";
import back from "@/assets/images/left.svg";
import surveyService from "../services/surveyService";
import useSurveyStore from "../stores/surveyStore";
import { validateAndBuildAnswersWithErrors } from "../utils/pollValidation";
import { PollAnswer } from "../types/common";

export interface PollProps {
  preview?: boolean;
}

const Poll: React.FC<PollProps> = ({ preview }) => {
  let { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const errorRef = useRef(null);
  const initSurvey = useSurveyStore.use.initSurvey();
  const survey = useSurveyStore.use.survey();
  const surveyQuestions = useSurveyStore.use.questions();
  const settings = useSurveyStore.use.settings();
  const surveyLoadingError = useSurveyStore.use.surveyLoadingError();
  const resetState = useSurveyStore.use.resetState();
  const clearSettings = useSurveyStore.use.clearSettings();

  const [questions, updateQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const refs = useRef<HTMLDivElement[]>([]);
  refs.current = [];
  const addToRefs = (el: HTMLDivElement) => {
    if (el && !refs.current.includes(el)) {
      refs.current.push(el);
    }
  };

  const markPollAsDropped = useCallback(() => {
    if (!preview && settings) {
      surveyService.updatePollStatus(settings, PollStatus.dropped);
    }
  }, [settings]);

  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      markPollAsDropped();
    };

    window.addEventListener("beforeunload", onUnload);

    return () => window.removeEventListener("beforeunload", onUnload);
  }, [markPollAsDropped]);

  useEffect(() => {
    if (location.pathname.toLowerCase().includes("preview")) {
      if (!id) {
        navigate("/");
      } else if (surveyQuestions.length === 0) {
        initSurvey(id, "Vb", true);
      }
    } else {
      // if there's no id in the URL or settings stored for this step
      // there's no way of determining the platform to use so we need to redirect
      if (!id || !settings) {
        navigate("/");
        // if there are no question we can try to refetch the survey
      } else if (surveyQuestions.length === 0) {
        initSurvey(id, settings.platform, false);
      }
    }
  }, []);

  useEffect(() => {
    updateQuestions(surveyQuestions);
  }, [surveyQuestions]);

  useEffect(() => {
    if (surveyLoadingError) {
      toast.error("Some data weren't loaded please refresh a page");
    }
  }, [surveyLoadingError]);

  const errorScroll = (errorIndex: number) => {
    refs.current.forEach((ref) => {
      if (
        ref.dataset.errorIndex &&
        parseInt(ref.dataset.errorIndex) === errorIndex
      ) {
        ref.scrollIntoView();
      }
    });
  };

  const setAnswer = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateQuestions(
      questions.map((question) => {
        if (question.children?.length) {
          let child = question.children.filter(
            (item) => item.question_id == e.target.dataset.questionId
          )[0];
          if (child) {
            child["value"] = e.target.value;
            child["error"] = false;
          }
        }

        // Radio Group
        if (question.question_id == e.target.dataset.questionId) {
          if (e.target.dataset.questionIndex) {
            if (!question.value || typeof question.value === "string") {
              question.value = {};
            }

            return {
              ...question,
              value: question.value
                ? {
                    ...question.value,
                    [e.target.dataset.questionIndex]: e.target.value,
                  }
                : { [e.target.dataset.questionIndex]: e.target.value },
              error: false,
            };
          } else {
            return { ...question, value: e.target.value, error: false };
          }
        }
        return question;
      })
    );
  };

  const goBack = () => {
    navigate(-1);
  };

  /**
   * Handles successful form submission
   */
  const handleSuccessfulSubmission = async (
    pollAnswers: PollAnswer[]
  ): Promise<void> => {
    if (preview) {
      navigate(`/preview_success/${id}`);
      return;
    }

    setIsLoading(true);
    try {
      await surveyService.submitAnswers(settings!, pollAnswers);
      clearSettings();
      navigate(`/success/${id}`);
    } catch (err) {
      setIsLoading(false);
      toast.error("Something went wrong, please try again, or refresh a page.");
    }
  };

  /**
   * Main form submission handler
   */
  const nextPage: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    // validate, build answers, and prepare error states
    const { hasErrors, answers, updatedQuestions, firstErrorIndex } =
      validateAndBuildAnswersWithErrors(questions, settings?.platform);
    if (hasErrors) {
      // Update questions with error states and scroll to first error
      updateQuestions(updatedQuestions);
      errorScroll(firstErrorIndex);
    } else {
      // Submit successful answers
      await handleSuccessfulSubmission(answers);
    }
  };

  const dropOff: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    markPollAsDropped();
    resetState();
    navigate(`/`);
  };

  let index = 0;

  return !survey ? null : (
    <div className={m.wrapper}>
      <Loader show={isLoading} />
      <form className={m.poll}>
        {survey.status !== undefined &&
        survey.status === SurveyStatus.CLOSED ? (
          <div className={m.borderLabel}>
            <h1>
              <LeftBorder />
              {"This survey was closed."}
            </h1>
          </div>
        ) : (
          <Fragment>
            <div ref={errorRef} className={m.pollTitle}>
              <h1>
                <img
                  src={back}
                  alt="back"
                  className={m.btnBack}
                  onClick={goBack}
                />
                {survey.title}
                <button className={m.btnExit} onClick={dropOff}>
                  Cancel
                </button>
              </h1>
              <label>Please answer the following questions.</label>
              <label>Please, be aware, some questions are required.</label>
            </div>
            {questions?.length > 0 &&
              questions.map((item) => {
                if (item.is_displayed) {
                  index++;
                  return (
                    <div
                      ref={addToRefs}
                      key={item.question_id}
                      data-error-index={index}
                    >
                      <PollQuestion
                        question={item}
                        index={index}
                        setAnswer={setAnswer}
                        companyName={survey.companyName}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            <div className={m.navigateButton}>
              <button className={`${m.btn} ${m.btnPrimary}`} onClick={nextPage}>
                Submit Results
              </button>
            </div>
          </Fragment>
        )}
      </form>
    </div>
  );
};
export default Poll;
